import {
  STAR_PLATFORM_PATHS,
  STARLINK_ROOT_ID,
  getStarlinkNode,
  isStarLearningEntryId,
  isStarLearningThemeId,
  starlinkAppRegistry,
  type StarlinkAppNode
} from "@zr-eduai/star-app-registry";

export { validateLearningPathNavigation, type StarLearningPathNavigationV1 } from './learning-path';
export const STAR_JOURNEY_CONTRACT_VERSION = "2.0.0" as const;

/**
 * The Journey module axis is a product contract, not a projection of the app
 * registry. Its order and two-character labels are deliberately stable so
 * every host renders the same five anchors, including unavailable modules.
 */
export const STAR_JOURNEY_MODULES = [
  { id: "observation", label: "观测" },
  { id: "experiment", label: "实验" },
  { id: "inquiry", label: "探究" },
  { id: "course", label: "课程" },
  { id: "showcase", label: "展示" }
] as const;

export type StarJourneyModuleId = (typeof STAR_JOURNEY_MODULES)[number]["id"];

export const STAR_LEARNING_LINE_IDS = ["ai-eco", "ai-sail", "ai-3d"] as const;
export type StarLearningLineId = (typeof STAR_LEARNING_LINE_IDS)[number];

/** Whether a module belongs to the selected learning line. */
export type StarJourneyApplicability = "supported" | "partial" | "planned" | "unsupported" | "unknown";
/** Whether the tenant has purchased the module. */
export type StarJourneyEntitlement = "entitled" | "unentitled" | "unknown";
/** Whether the current subject may enter the module. */
export type StarJourneyAuthorization = "authorized" | "unauthorized" | "anonymous" | "unknown";
/** Whether the destination can currently serve the module. */
export type StarJourneyRuntime = "ready" | "degraded" | "maintenance" | "unavailable" | "unknown";

export type StarJourneyIntroReferenceV2 = Readonly<{
  introId: string;
  introVersion: string;
  poster?: string;
}>;

/**
 * Host-supplied facts for one stable Journey anchor. The four axes stay
 * independent: Star UI may derive a presentation state, but must not infer one
 * axis from another. `href` is always the real canonical launcher/destination
 * URL and remains usable for every presentation state; `explanationHref` is an
 * optional secondary detail route, never a replacement for that real link.
 * `launchPolicy` only describes launcher behavior for unresolved facts; it
 * never changes the entitlement, authorization, or runtime values themselves.
 */
export type StarJourneyModuleSnapshotV2 = Readonly<{
  learningLineId: StarLearningLineId;
  moduleId: StarJourneyModuleId;
  applicability: StarJourneyApplicability;
  entitlement: StarJourneyEntitlement;
  authorization: StarJourneyAuthorization;
  runtime: StarJourneyRuntime;
  launchPolicy?: "verify-on-entry";
  href: string;
  explanationHref?: string;
  targetAppId?: string;
  reason?: string;
  intro?: StarJourneyIntroReferenceV2;
}>;

export type StarJourneyResolvedState =
  | "planned"
  | "unsupported"
  | "anonymous"
  | "unentitled"
  | "unauthorized"
  | "maintenance"
  | "unavailable"
  | "degraded"
  | "verify-on-entry"
  | "unknown"
  | "partial"
  | "available";

const JOURNEY_STATE_LABELS: Readonly<Record<StarJourneyResolvedState, string>> = {
  planned: "规划中",
  unsupported: "暂不适用",
  anonymous: "登录后查看",
  unentitled: "未购买",
  unauthorized: "无权限",
  maintenance: "维护中",
  unavailable: "暂不可用",
  degraded: "部分可用",
  "verify-on-entry": "进入时校验",
  unknown: "状态未知",
  partial: "部分开放",
  available: "已开通"
};

export type NavigationState = "available" | "locked" | "maintenance";

export type NavigationContext = {
  allowedApps?: ReadonlySet<string>;
  maintenanceApps?: ReadonlySet<string>;
  starlinkBaseUrl: string;
  ecoBaseUrl: string;
};

export function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function isStarJourneyModuleId(value: string | null | undefined): value is StarJourneyModuleId {
  return STAR_JOURNEY_MODULES.some((module) => module.id === value);
}

export function isStarLearningLineId(value: string | null | undefined): value is StarLearningLineId {
  return (STAR_LEARNING_LINE_IDS as readonly string[]).includes(value ?? "");
}

/**
 * Build the canonical Console launcher route. Invalid axes return `undefined`
 * so callers cannot accidentally publish an unreachable matrix URL.
 */
export function journeyModuleHref(
  learningLineId: string | null | undefined,
  moduleId: string | null | undefined,
  starlinkBaseUrl: string
): string | undefined {
  if (!isStarLearningLineId(learningLineId) || !isStarJourneyModuleId(moduleId)) return undefined;
  return `${normalizeBaseUrl(starlinkBaseUrl)}/themes/${learningLineId}/modules/${moduleId}`;
}

/**
 * Resolve the compact visual state without mutating or replacing any source
 * fact. Earlier checks intentionally have higher presentation priority.
 */
export function resolveJourneyModuleState(snapshot: StarJourneyModuleSnapshotV2): StarJourneyResolvedState {
  if (snapshot.applicability === "planned") return "planned";
  if (snapshot.applicability === "unsupported") return "unsupported";
  if (snapshot.authorization === "anonymous") return "anonymous";
  if (snapshot.entitlement === "unentitled") return "unentitled";
  if (snapshot.authorization === "unauthorized") return "unauthorized";
  if (snapshot.runtime === "maintenance") return "maintenance";
  if (snapshot.runtime === "unavailable") return "unavailable";
  if (snapshot.runtime === "degraded") return "degraded";

  if (
    (snapshot.applicability === "supported" || snapshot.applicability === "partial")
    && snapshot.authorization === "authorized"
    && snapshot.launchPolicy === "verify-on-entry"
    && (snapshot.entitlement === "unknown" || snapshot.runtime === "unknown")
  ) return "verify-on-entry";

  if (
    !["supported", "partial"].includes(snapshot.applicability)
    || snapshot.entitlement !== "entitled"
    || snapshot.authorization !== "authorized"
    || snapshot.runtime !== "ready"
  ) return "unknown";

  return snapshot.applicability === "partial" ? "partial" : "available";
}

export function journeyStateLabel(state: StarJourneyResolvedState): string {
  return JOURNEY_STATE_LABELS[state];
}

export function resolveStarlinkHref(node: StarlinkAppNode, context: Pick<NavigationContext, "starlinkBaseUrl" | "ecoBaseUrl">): string {
  const base = normalizeBaseUrl(node.destination === "eco" ? context.ecoBaseUrl : context.starlinkBaseUrl);
  return `${base}${node.launchPath.startsWith("/") ? node.launchPath : `/${node.launchPath}`}`;
}

export function resolvePlatformHref(path: string, starlinkBaseUrl: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizeBaseUrl(starlinkBaseUrl)}${normalizedPath}`;
}

function learningSelectionPath(themeId?: string | null, entryId?: string | null): string {
  const validThemeId = isStarLearningThemeId(themeId) ? themeId : undefined;
  const validEntryId = isStarLearningEntryId(entryId) ? entryId : undefined;

  if (validThemeId && validEntryId) return `/themes/${validThemeId}/entries/${validEntryId}`;
  if (validThemeId) return `/themes/${validThemeId}`;
  if (validEntryId) return `/entries/${validEntryId}`;
  return STAR_PLATFORM_PATHS.catalog;
}

/** Select or clear only the learning-theme axis, preserving a valid app axis. */
export function themeSelectionHref(
  themeId: string | null | undefined,
  currentEntryId: string | null | undefined,
  starlinkBaseUrl: string
): string {
  return resolvePlatformHref(learningSelectionPath(themeId, currentEntryId), starlinkBaseUrl);
}

/** Select or clear only the learning-app axis, preserving a valid theme axis. */
export function entrySelectionHref(
  entryId: string | null | undefined,
  currentThemeId: string | null | undefined,
  starlinkBaseUrl: string
): string {
  return resolvePlatformHref(learningSelectionPath(currentThemeId, entryId), starlinkBaseUrl);
}

export function hasPermission(allowedApps: ReadonlySet<string>, permission: string): boolean {
  if (allowedApps.has(permission)) return true;
  if (["world-view", "ai-eco"].includes(permission)) {
    return Array.from(allowedApps).some((allowed) => allowed.startsWith(`${permission}:`));
  }
  const family = permission.split(":", 1)[0];
  return ["world-view", "ai-eco"].includes(family) && allowedApps.has(family);
}

export function navigationState(node: StarlinkAppNode, context: NavigationContext): NavigationState {
  if (node.status === "maintenance" || context.maintenanceApps?.has(node.id)) return "maintenance";
  if (context.allowedApps
      && node.id !== STARLINK_ROOT_ID
      && !hasPermission(context.allowedApps, node.permission ?? node.id)) return "locked";
  return "available";
}

export function parentNodeFor(currentAppId: string): StarlinkAppNode | undefined {
  const node = getStarlinkNode(currentAppId);
  return node?.parentId ? getStarlinkNode(node.parentId) : undefined;
}

export function parentReturnFor(currentAppId: string, starlinkBaseUrl: string, ecoBaseUrl: string): { href: string; label: string } {
  const parent = parentNodeFor(currentAppId) ?? getStarlinkNode(STARLINK_ROOT_ID)!;
  if (parent.id === "ai-eco") {
    return { href: `${normalizeBaseUrl(ecoBaseUrl)}/`, label: "返回格物工作台" };
  }
  return {
    href: resolveStarlinkHref(parent, { starlinkBaseUrl, ecoBaseUrl }),
    label: parent.id === STARLINK_ROOT_ID ? "返回产品总览" : `返回${parent.name}`
  };
}

export function applicationDestinationCount(): number {
  return starlinkAppRegistry.filter((node) => node.kind === "app" || node.kind === "subapp").length;
}
